import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { preserveMathSquareBrackets } from "../../utils/mathmlMatrixUtils";
import "./CkEditor.css";

const API_URL = "https://qms.motion.ac.in/apiseq/api/v1";
const UPLOAD_ENDPOINT = "common/fileupload";

const IMAGE_TOOLBAR_WITH_PIXEL_SIZE = [
  "imagePixelSize",
  "-",
  "resizeImage",
  "|",
  "imageStyle:inline",
  "imageStyle:block",
  "imageStyle:side",
  "|",
  "toggleImageCaption",
  "imageTextAlternative",
];

const userToken = JSON.parse(localStorage.getItem("user"))?.accessToken;

/**
 * CKEditor + MathType must NOT receive a live `data` prop after mount.
 * Updating `data` from React state (on every onChange) triggers editor.data.set(),
 * which re-parses formulas through Wiris and breaks matrix brackets on insert.
 * Use `editorKey` to remount when loading new content from the API.
 */
const MyEditor = forwardRef(
  (
    {
      handleChange,
      data,
      editorKey = "editor",
      onInit,
      className = "",
      enablePixelImageSize = false,
      ...rest
    },
    ref
  ) => {
    const editorRef = useRef(null);
    const initialDataRef = useRef(data || "");
    const loadedKeyRef = useRef(editorKey);

    if (editorKey !== loadedKeyRef.current) {
      loadedKeyRef.current = editorKey;
      initialDataRef.current = data || "";
    }

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (!editorRef.current) {
          return preserveMathSquareBrackets(initialDataRef.current || "");
        }
        const html = editorRef.current.getData();
        return preserveMathSquareBrackets(html, editorRef.current);
      },
    }));

    const [isFullScreen, setIsFullScreen] = useState(false);
    function uploadAdapter(loader) {
      return {
        upload: () => {
          return loader.file.then((file) => {
            const body = new FormData();
            body.append("file", file);

            return fetch(`${API_URL}/${UPLOAD_ENDPOINT}`, {
              method: "POST",
              body: body,
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
            })
              .then(async (res) => {
                if (!res.ok) {
                  const text = await res.text();
                  throw new Error(text);
                }
                return res.json();
              })
              .then((res) => ({
                default: `https://cdnqms.motion.ac.in/${res.data.key}`,
              }));
          });
        },
      };
    }

    function uploadPlugin(editor) {
      editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
        return uploadAdapter(loader);
      };
    }

    const ckEditorConfig = {
      extraPlugins: [uploadPlugin],
      initialData: initialDataRef.current,
      toolbar: {
        shouldNotGroupWhenFull: true,
      },
      ...(enablePixelImageSize
        ? {
            image: {
              // Keep the existing % resize system and add a native px row above it.
              // The custom CKEditor build still owns resizeUnit: "%" and the
              // existing 25/50/75% resize options.
              toolbar: IMAGE_TOOLBAR_WITH_PIXEL_SIZE,
            },
          }
        : {}),
    };

    return (
      <div className={`qm-ckeditor ${isFullScreen ? "editor-fullscreen" : "editor-normal"} ${className}`}>
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}
        >
          <button
            onClick={() => setIsFullScreen(!isFullScreen)}
            title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "6px",
            }}
          >
            {isFullScreen ? (
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#333"
                  d="M14 14h5v5h-2v-3h-3v-2zm-4 0v2H7v3H5v-5h5zm9-9v5h-2V7h-3V5h5zM5 5h5v2H7v3H5V5z"
                />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#333"
                  d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H5v5zm10 9h-3v2h5v-5h-2v3zm-3-14v2h3v3h2V5h-5z"
                />
              </svg>
            )}
          </button>
        </div>
        <div>
          <CKEditor
            key={editorKey}
            editor={Editor}
            onInit={onInit}
            onReady={(editor) => {
              editorRef.current = editor;
            }}
            onChange={(event, editor) => {
              handleChange?.(editor.getData());
            }}
            config={ckEditorConfig}
            onBlur={(event, editor) => {
              handleChange?.(editor.getData());
            }}
            {...rest}
          />
        </div>
      </div>
    );
  }
);

MyEditor.displayName = "MyEditor";

export default MyEditor;
